import Prism from 'prismjs'

/**
 * Dependencies that must be loaded before certain languages.
 * Prism languages often extend other languages and require them to be loaded first.
 */
const languageDependencies: Record<string, string[]> = {
    // JavaScript and TypeScript
    javascript: [],
    js: [],
    typescript: ['javascript'],
    ts: ['javascript'],

    // JSX/TSX
    jsx: ['javascript'],
    tsx: ['javascript', 'typescript', 'jsx'],

    // Python
    python: [],
    py: [],

    // Solidity (extends clike)
    solidity: ['clike'],
    sol: ['clike'],
}

/**
 * Map of language names to their Prism component import functions.
 * Languages are lazy-loaded on demand.
 */
/* eslint-disable @typescript-eslint/ban-ts-comment */
const languageLoaders: Record<string, () => Promise<unknown>> = {
    // @ts-ignore - prismjs components don't have type declarations
    clike: () => import('prismjs/components/prism-clike'),
    // @ts-ignore - prismjs components don't have type declarations
    css: () => import('prismjs/components/prism-css'),
    // @ts-ignore
    diff: () => import('prismjs/components/prism-diff'),
    // @ts-ignore
    go: () => import('prismjs/components/prism-go'),
    // @ts-ignore
    javascript: () => import('prismjs/components/prism-javascript'),
    // @ts-ignore
    js: () => import('prismjs/components/prism-javascript'),
    // @ts-ignore
    json: () => import('prismjs/components/prism-json'),
    // @ts-ignore
    jsx: () => import('prismjs/components/prism-jsx'),
    // @ts-ignore
    python: () => import('prismjs/components/prism-python'),
    // @ts-ignore
    py: () => import('prismjs/components/prism-python'),
    // @ts-ignore
    rust: () => import('prismjs/components/prism-rust'),
    // @ts-ignore
    sol: () => import('prismjs/components/prism-solidity'),
    // @ts-ignore
    solidity: () => import('prismjs/components/prism-solidity'),
    // @ts-ignore
    tsx: () => import('prismjs/components/prism-tsx'),
    // @ts-ignore
    typescript: () => import('prismjs/components/prism-typescript'),
    // @ts-ignore
    ts: () => import('prismjs/components/prism-typescript'),
}
/* eslint-enable @typescript-eslint/ban-ts-comment */

// Track which languages are currently being loaded to avoid duplicate imports
// Store the promise so concurrent callers can await the same load operation
const loadingLanguages = new Map<string, Promise<boolean>>()

/**
 * Load a Prism language component if not already loaded.
 * Returns true if the language is available (either already loaded or successfully loaded).
 */
export async function loadPrismLanguage(lang: string): Promise<boolean> {
    // Already loaded
    if (Prism.languages[lang]) {
        return true
    }

    const loader = languageLoaders[lang]
    if (!loader) {
        return false
    }

    // If already loading, await the existing promise
    const existingLoad = loadingLanguages.get(lang)
    if (existingLoad) {
        return existingLoad
    }

    // Create a new loading promise
    const loadPromise = (async () => {
        try {
            // Load dependencies first (e.g., tsx requires typescript and jsx)
            const deps = languageDependencies[lang]
            if (deps) {
                for (const dep of deps) {
                    await loadPrismLanguage(dep)
                }
            }

            await loader()
            return Prism.languages[lang] !== undefined
        } catch {
            return false
        } finally {
            loadingLanguages.delete(lang)
        }
    })()

    loadingLanguages.set(lang, loadPromise)
    return loadPromise
}

/**
 * Check if a language is supported (has a loader defined).
 */
export function isSupportedLanguage(lang: string): boolean {
    return lang in languageLoaders || lang in Prism.languages
}

/**
 * Get all supported language names.
 */
export function getSupportedLanguages(): string[] {
    return Object.keys(languageLoaders)
}
